import React, { useEffect, useMemo, useState } from 'react';
import { getCalculatorsColumns } from '../../../../helpers/constants';
import DataTable from '../../../common/data-table/DataTable';
import AppModal from '../../../common/AppModal';
import AddEditCalculatorForm from './AddEditCalculatorForm';
import useLocalization from '../../../../hooks/useLocalization';
import { updateItemsInArray } from '../../../../helpers/global';
import { calculatorService } from '../../../../services/calculatorServices';
import { toast } from 'react-toastify';

const AddEditCalculatorSideBar = ({
  calculator,
  onChange,
  setEditCalculatorMeta,
  setIsCalculatorEdited,
  calculatorCategories,
  setCalculatorCategories
}) => {
  const { translate } = useLocalization();
  const [formData, setFormData] = useState([]);
  const [showAddEditFieldModalMeta, setShowAddEditFieldModalMeta] = useState(null);
  const [disableDragging, setDisableDragging] = useState(false);
  const [allCalculatorFieldCategories, setAllCalculatorFieldCategories] = useState([]);

  const isRSUorOptionsCalculator = useMemo(() => {
    return calculator?.type === 'rsuCalculator' || calculator?.type === 'optionsCalculator';
  }, [calculator]);

  const optionsAccordingToCategory = useMemo(() => {
    const groups = calculatorCategories.map(c => ({
      category: c.key,
      options: formData?.filter(o => o.category === c.key).map((o, index) => ({ ...o, index }))
    }));
    return groups;
  }, [formData, calculatorCategories]);

  const onEditFieldClick = field => {
    setShowAddEditFieldModalMeta({ field, mode: 'edit' });
  };

  const columns = useMemo(
    () =>
      getCalculatorsColumns({
        onEditOptionClick: onEditFieldClick,
        onDeleteOptionClick: () => {},
        allowEdit: true,
        allowDelete: true
      }),
    []
  );

  const onFieldDragEnd = async ({ destination, source }) => {
    try {
      setDisableDragging(true);

      let updatedOptions;

      if (isRSUorOptionsCalculator) {
        //does not need organizing by category
        updatedOptions = [...formData];
        const [movedItem] = updatedOptions.splice(source.index, 1);
        updatedOptions.splice(destination.index, 0, movedItem);
      } else {
        //needs organizing by category
        if (destination.droppableId === source.droppableId) {
          let destinationOptionsArray = optionsAccordingToCategory.find(s => s.category === destination.droppableId)
            .options;

          const [movedItem] = destinationOptionsArray.splice(source.index, 1);
          destinationOptionsArray.splice(destination.index, 0, movedItem);

          const updatedCategory = {
            category: destination.droppableId,
            options: destinationOptionsArray
          };

          updatedOptions = updateItemsInArray(optionsAccordingToCategory, updatedCategory, 'category').flatMap(
            o => o?.options
          );
        } else {
          let sourceOptionsArray = optionsAccordingToCategory.find(s => s.category === source.droppableId)?.options;

          let destinationOptionsArray = optionsAccordingToCategory.find(s => s.category === destination.droppableId)
            ?.options;

          const [movedItem] = sourceOptionsArray.splice(source.index, 1);
          movedItem.category = destination.droppableId;

          destinationOptionsArray.splice(destination.index, 0, movedItem);

          const sourceOptionsCategory = {
            category: source.droppableId,
            options: sourceOptionsArray
          };
          const destinationOptionsCategory = {
            category: destination.droppableId,
            options: destinationOptionsArray
          };

          updatedOptions = updateItemsInArray(
            optionsAccordingToCategory,
            [sourceOptionsCategory, destinationOptionsCategory],
            'category'
          ).flatMap(s => s?.options);
        }
      }

      setFormData(updatedOptions);

      const updatedCalculator = {
        ...calculator,
        fields: updatedOptions.map((o, index) => ({ ...o, index }))
      };

      setEditCalculatorMeta({ calculator: updatedCalculator });
      setIsCalculatorEdited(true);
      setTimeout(() => {
        setDisableDragging(false);
      }, 100);
    } catch (error) {
      console.log(error);
    } finally {
      setDisableDragging(false);
    }
  };

  const handleAddEditFieldSubmit = async values => {
    const newData = formData.map((o, index) => {
      if (o._id === showAddEditFieldModalMeta?.field?._id) {
        return {
          ...o,
          ...values,
          index
        };
      }
      return {
        ...o,
        index
      };
    });

    setFormData(newData);
    const updatedCalculator = {
      ...calculator,
      fields: newData
    };
    setEditCalculatorMeta({ calculator: updatedCalculator });
    setIsCalculatorEdited(true);

    setShowAddEditFieldModalMeta(null);
  };

  const handleGroupHeaderClick = group => {
    const categoryObj = calculatorCategories.find(cat => cat.key === group?.category);

    if (!categoryObj) {
      toast.error('Category not found');
      return;
    }

    setShowAddEditFieldModalMeta({
      field: categoryObj,
      mode: 'editCategory',
      categoryObj
    });
  };

  const handleCategorySubmit = async values => {
    // Get the original category object
    const originalCategory = showAddEditFieldModalMeta?.categoryObj;

    if (!originalCategory) {
      return toast.error('Category data not found');
    }

    const updatedCategory = {
      ...originalCategory,
      english: values.english,
      hebrew: values.hebrew
    };
    setCalculatorCategories(prev => {
      const updatedCategories = prev.map(cat => (cat._id === updatedCategory._id ? updatedCategory : cat));
      return updatedCategories;
    });
    setIsCalculatorEdited(true);
    setShowAddEditFieldModalMeta(null);
  };

  const handleFormSubmit = async values => {
    if (showAddEditFieldModalMeta?.mode === 'editCategory') {
      return handleCategorySubmit(values);
    } else {
      return handleAddEditFieldSubmit(values);
    }
  };

  useEffect(() => {
    if (calculator) {
      setFormData(calculator.fields || []);
    }
  }, [calculator]);

  useEffect(() => {
    const fetchCategories = async () => {
      const reqBody = {
        calcType: calculator?.type
      };
      try {
        const { response } = await calculatorService.getCategories(reqBody);
        setCalculatorCategories(response);
      } catch (error) {
        console.log(error);
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="">
      <DataTable
        allowFilter={false}
        allowSort={false}
        rowKey={'_id'}
        draggable
        columns={columns}
        data={formData}
        onDragEnd={onFieldDragEnd}
        groupBy={!isRSUorOptionsCalculator ? 'category' : null}
        groups={
          !isRSUorOptionsCalculator
            ? calculatorCategories.map(c => ({
                category: c.key,
                label: c.english,
                hebrewLabel: c.hebrew
              }))
            : []
        }
        disableDragging={disableDragging}
        allowGroupNameEdit={true}
        onGroupHeaderClick={handleGroupHeaderClick}
      />

      {/* Add/Edit Field & Group Name Modal */}
      <AppModal
        title={`Add/Edit ${showAddEditFieldModalMeta?.mode === 'editCategory' ? 'Category' : 'Field'}`}
        show={Boolean(showAddEditFieldModalMeta)}
        onHide={() => setShowAddEditFieldModalMeta(null)}
      >
        <AddEditCalculatorForm
          editMode={showAddEditFieldModalMeta?.mode === 'edit'}
          initialValues={showAddEditFieldModalMeta?.field}
          onSubmit={handleFormSubmit}
          translate={translate}
          calculator={calculator}
          isGroup={showAddEditFieldModalMeta?.mode === 'editCategory'}
        />
      </AppModal>
    </div>
  );
};

export default AddEditCalculatorSideBar;
